const BUYER_PLATFORM_FEE_RATE = 0.10;
const SELLER_COMMISSION_RATE = 0.15;

const roundToCents = (value) => Math.round(value * 100) / 100;

export const getBuyerDisplayPrice = (basePrice, estimatedShipping = 0) => {
  const value = Number.parseFloat(basePrice);
  if (!Number.isFinite(value) || value <= 0) return null;
  const shipping = Number.parseFloat(estimatedShipping) || 0;
  return (value + shipping) * (1 + BUYER_PLATFORM_FEE_RATE);
};

export const formatBuyerDisplayPrice = (basePrice, { estimatedShipping = 0, round = true } = {}) => {
  const buyerPrice = getBuyerDisplayPrice(basePrice, estimatedShipping);
  if (buyerPrice === null) return '';
  const displayValue = round ? Math.round(buyerPrice) : Math.round(buyerPrice * 100) / 100;
  return `$${displayValue.toLocaleString()}`;
};

const normalizeUnit = (unit) => {
  const normalized = String(unit || 'in').toLowerCase();
  if (['cm', 'centimeter', 'centimeters'].includes(normalized)) return 'cm';
  return 'in';
};

const toInches = (value, unit) => {
  const numeric = Number.parseFloat(value);
  if (!Number.isFinite(numeric) || numeric <= 0) return 0;
  return normalizeUnit(unit) === 'cm' ? numeric / 2.54 : numeric;
};

export const estimateArtworkShipping = (dimensions = {}) => {
  const unit = normalizeUnit(dimensions.unit || dimensions.units);
  const source = {
    width: toInches(dimensions.width, unit),
    height: toInches(dimensions.height, unit),
    depth: toInches(dimensions.depth || 0, unit)
  };
  const packaged = [
    source.width + 6,
    source.height + 6,
    Math.max(source.depth, 2) + 4
  ].sort((a, b) => b - a);

  const packagedDimensions = {
    length: roundToCents(packaged[0] || 0),
    width: roundToCents(packaged[1] || 0),
    height: roundToCents(packaged[2] || 0)
  };

  const dimensionalWeight = packagedDimensions.length && packagedDimensions.width && packagedDimensions.height
    ? Math.ceil((packagedDimensions.length * packagedDimensions.width * packagedDimensions.height) / 139)
    : 0;
  const girth = packagedDimensions.length + 2 * (packagedDimensions.width + packagedDimensions.height);
  const longestSide = packagedDimensions.length;

  if (!longestSide || !packagedDimensions.width || !packagedDimensions.height) {
    return { amount: 150, tier: 'unknown', manualReview: false };
  }
  if (longestSide <= 24 && girth <= 72 && dimensionalWeight <= 10) {
    return { amount: 45, tier: 'small', manualReview: false };
  }
  if (longestSide <= 36 && girth <= 96 && dimensionalWeight <= 25) {
    return { amount: 85, tier: 'medium', manualReview: false };
  }
  if (longestSide <= 48 && girth <= 120 && dimensionalWeight <= 50) {
    return { amount: 175, tier: 'large', manualReview: false };
  }
  if (longestSide <= 72 && girth <= 150 && dimensionalWeight <= 90) {
    return { amount: 350, tier: 'oversize', manualReview: false };
  }
  return { amount: 750, tier: 'freight_review', manualReview: true };
};

export const calculateArtworkPricing = (basePrice, dimensions = {}) => {
  const artistBasePrice = Number.parseFloat(basePrice);
  if (!Number.isFinite(artistBasePrice) || artistBasePrice <= 0) {
    return null;
  }

  const shippingEstimate = estimateArtworkShipping(dimensions);
  const subtotalBeforePlatformFee = artistBasePrice + shippingEstimate.amount;
  const buyerPlatformFee = subtotalBeforePlatformFee * BUYER_PLATFORM_FEE_RATE;
  const buyerDisplayPrice = subtotalBeforePlatformFee + buyerPlatformFee;
  const sellerCommission = artistBasePrice * SELLER_COMMISSION_RATE;

  return {
    artistBasePrice: roundToCents(artistBasePrice),
    estimatedShipping: roundToCents(shippingEstimate.amount),
    shippingTier: shippingEstimate.tier,
    manualReview: shippingEstimate.manualReview,
    buyerPlatformFee: roundToCents(buyerPlatformFee),
    buyerDisplayPrice: roundToCents(buyerDisplayPrice),
    sellerCommission: roundToCents(sellerCommission),
    sellerNetAfterCommission: roundToCents(artistBasePrice - sellerCommission)
  };
};
